import React from 'react';
import {
  Alert,
  Box,
  Grid,
  Typography,
  Fade,
  Grow,
  Stack,
  alpha
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LinkIcon from '@mui/icons-material/Link';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import type { DriveResource } from '../types';
import { DriveResourceCard } from './DriveResourceCard';

interface DriveResourceListProps {
  resources: DriveResource[];
  loading: boolean;
  error: Error | null;
  onEdit: (resource: DriveResource) => void;
  onDelete: (resource: DriveResource) => void;
  canManage?: boolean;
}

export const DriveResourceList: React.FC<DriveResourceListProps> = ({
  resources,
  loading,
  error,
  onEdit,
  onDelete,
  canManage = true
}) => {
  if (loading && resources.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading resources...
      </Typography>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load resources. {error.message}
      </Alert>
    );
  }

  if (resources.length === 0) {
    return (
      <Box
        sx={{
          border: '2px dashed',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
          borderRadius: 4,
          p: 6,
          textAlign: 'center',
          background: (theme) => `linear-gradient(135deg,
            ${alpha(theme.palette.primary.main, 0.03)} 0%,
            ${alpha(theme.palette.primary.light, 0.01)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative Background */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
            borderRadius: '50%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
            borderRadius: '50%',
          }}
        />

        {/* Content */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Icon */}
          <Box
            sx={{
              display: 'inline-flex',
              p: 3,
              borderRadius: '50%',
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
              mb: 3,
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: '#fff' }} />
          </Box>

          <Typography
            variant="h6"
            fontWeight={700}
            gutterBottom
            sx={{
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Your resource library awaits
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            Start building your team's knowledge base by adding documents, links, or Google Drive resources.
            Everything you add here will be organized and easily accessible.
          </Typography>

          {/* Quick Tips */}
          <Stack spacing={1.5} sx={{ mt: 4, textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: (theme) => alpha('#2196f3', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.5,
                }}
              >
                <InsertDriveFileIcon sx={{ fontSize: 16, color: '#2196f3' }} />
              </Box>
              <Box flex={1}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Upload Files
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PDFs, documents, spreadsheets, and presentations
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: (theme) => alpha('#4caf50', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.5,
                }}
              >
                <FolderSharedIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              </Box>
              <Box flex={1}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Link Google Drive
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Connect shared Google Drive files and folders
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: (theme) => alpha('#9c27b0', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.5,
                }}
              >
                <LinkIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
              </Box>
              <Box flex={1}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  External Links
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add links to videos, websites, or online tools
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Grid container spacing={2.5}>
      {resources.map((resource, index) => (
        <Grid item xs={12} sm={6} md={4} key={resource.id}>
          <Grow
            in={true}
            style={{ transformOrigin: '0 0 0' }}
            timeout={300 + index * 50}
          >
            <Box>
              <DriveResourceCard
                resource={resource}
                onEdit={onEdit}
                onDelete={onDelete}
                canManage={canManage}
              />
            </Box>
          </Grow>
        </Grid>
      ))}
    </Grid>
  );
};

export default DriveResourceList;
